import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Star, Users, Sparkles, LogOut, Flame, Award, 
  ChevronRight, Calendar, HelpCircle, FolderOpen, Target, 
  Briefcase, CheckCircle, Brain, MapPin, ArrowLeft, Zap, Circle
} from 'lucide-react';

// YAS Design V4: "Bold Energy"
// Most playful - Bento grid, sticker-like elements, dynamic layout
// Playful: Asymmetric grid, sticker accents, bold typography, pop colors

const YAS_LOGO = '/yas-logo.png';

const YASDesignV4 = () => {
  const navigate = useNavigate();

  const colors = {
    teal: '#1B2D4F',
    tealLight: '#2A8A9D',
    tealPop: '#00D4AA',
    orange: '#E86C3D',
    orangeLight: '#FF9066',
    yellow: '#FFD93D',
    cream: '#FEF9F3',
    navy: '#1A3A4A',
    coral: '#FF6B6B',
    lavender: '#E8E0FF',
  };

  return (
    <div className="min-h-screen pb-24" style={{ 
      backgroundColor: colors.cream,
      fontFamily: "'Quicksand', sans-serif"
    }}>
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 bg-white rounded-2xl p-2.5 shadow-lg border-2 hover:scale-105 transition-transform"
        style={{ borderColor: colors.navy }}
      >
        <ArrowLeft className="w-5 h-5" style={{ color: colors.navy }} />
      </button>

      {/* Theme Label */}
      <div 
        className="fixed top-4 right-4 z-50 px-4 py-2 rounded-2xl font-bold text-sm shadow-lg border-2"
        style={{ backgroundColor: colors.yellow, borderColor: colors.navy, color: colors.navy }}
      >
        YAS V4: Bold Energy
      </div>

      {/* Hero Header - Asymmetric */}
      <div className="relative overflow-hidden pb-6" style={{ backgroundColor: colors.teal }}>
        {/* Sticker-like shapes */}
        <div 
          className="absolute top-8 right-4 w-16 h-16 rounded-2xl border-2 flex items-center justify-center"
          style={{ backgroundColor: colors.yellow, borderColor: colors.navy, transform: 'rotate(12deg)' }}
        >
          <Star className="w-8 h-8" style={{ color: colors.navy }} fill="currentColor" />
        </div>
        <div 
          className="absolute top-24 right-20 w-10 h-10 rounded-full border-2"
          style={{ backgroundColor: colors.coral, borderColor: colors.navy }}
        />
        <div 
          className="absolute bottom-16 left-4 w-8 h-8 rounded-lg border-2"
          style={{ backgroundColor: colors.tealPop, borderColor: colors.navy, transform: 'rotate(-8deg)' }}
        />
        
        <div className="relative px-4 pt-20 pb-4">
          <div className="max-w-md mx-auto">
            {/* Logo + Welcome in Bento style */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {/* Logo Card */}
              <div 
                className="col-span-1 bg-white rounded-3xl p-3 border-2 flex items-center justify-center shadow-lg"
                style={{ borderColor: colors.navy }}
              >
                <img src={YAS_LOGO} alt="YAS" className="w-full h-auto object-contain" />
              </div>
              
              {/* Welcome Card */}
              <div 
                className="col-span-2 bg-white rounded-3xl p-4 border-2 shadow-lg"
                style={{ borderColor: colors.navy }}
              >
                <p className="text-xs font-bold mb-1" style={{ color: colors.orange }}>YO, WHAT'S GOOD</p>
                <h1 className="text-2xl font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>
                  BRANDON
                </h1>
                <div className="flex items-center gap-2 mt-2">
                  <span 
                    className="text-xs font-bold px-2 py-1 rounded-lg border-2"
                    style={{ backgroundColor: colors.orange, borderColor: colors.navy, color: 'white' }}
                  >
                    UpNEXT
                  </span>
                  <span 
                    className="text-xs font-bold px-2 py-1 rounded-lg border-2"
                    style={{ backgroundColor: colors.tealPop, borderColor: colors.navy, color: colors.navy }}
                  >
                    7 Day Streak
                  </span>
                </div>
              </div>
            </div>

            {/* Stats + Location Bento */}
            <div className="grid grid-cols-2 gap-3">
              {/* Big Points Card */}
              <div 
                className="bg-white rounded-3xl p-4 border-2 shadow-lg relative overflow-hidden"
                style={{ borderColor: colors.navy }}
              >
                <div 
                  className="absolute -top-2 -right-2 w-12 h-12 rounded-full"
                  style={{ backgroundColor: colors.yellow, opacity: 0.5 }}
                />
                <div className="relative">
                  <div className="flex items-center gap-1 mb-1">
                    <Star className="w-4 h-4" style={{ color: colors.orange }} fill="currentColor" />
                    <span className="text-xs font-bold" style={{ color: colors.teal }}>POINTS</span>
                  </div>
                  <p className="text-4xl font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>
                    1,250
                  </p>
                </div>
              </div>

              {/* Location Card */}
              <div 
                className="rounded-3xl p-4 border-2 shadow-lg"
                style={{ backgroundColor: colors.orange, borderColor: colors.navy }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-white" />
                  <span className="text-xs font-bold text-white/80">YOUR SPOT</span>
                </div>
                <p className="text-sm font-bold text-white leading-tight">
                  Newark Public Library
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Jagged bottom edge */}
        <svg className="absolute bottom-0 left-0 right-0" viewBox="0 0 400 20" preserveAspectRatio="none">
          <path d="M0 20 L20 5 L40 15 L60 5 L80 18 L100 3 L120 15 L140 8 L160 18 L180 5 L200 15 L220 3 L240 18 L260 8 L280 15 L300 5 L320 18 L340 8 L360 15 L380 5 L400 20 Z" fill={colors.cream} />
        </svg>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-4 space-y-4">
        
        {/* Quick Actions - Sticker style grid */}
        <div className="grid grid-cols-6 gap-2">
          {[
            { icon: Target, label: 'Goals', bg: colors.tealPop },
            { icon: Briefcase, label: 'Project', bg: colors.orange },
            { icon: Trophy, label: 'Ranks', bg: colors.yellow },
            { icon: Award, label: 'Wins', bg: colors.coral },
            { icon: FolderOpen, label: 'Files', bg: colors.lavender },
            { icon: HelpCircle, label: 'Help', bg: colors.teal },
          ].map((item, idx) => (
            <button
              key={item.label}
              className="aspect-square rounded-2xl border-2 flex flex-col items-center justify-center shadow-md hover:scale-105 transition-transform"
              style={{ 
                backgroundColor: item.bg, 
                borderColor: colors.navy,
                transform: `rotate(${(idx % 2 === 0 ? -3 : 3)}deg)`
              }}
            >
              <item.icon className="w-5 h-5" style={{ color: item.bg === colors.yellow || item.bg === colors.lavender ? colors.navy : 'white' }} />
              <p className="text-[10px] font-bold mt-1" style={{ color: item.bg === colors.yellow || item.bg === colors.lavender ? colors.navy : 'white' }}>
                {item.label}
              </p>
            </button>
          ))}
        </div>

        {/* Work Journal - Big sticker card */}
        <div 
          className="rounded-3xl p-5 border-2 relative overflow-hidden shadow-xl"
          style={{ backgroundColor: colors.yellow, borderColor: colors.navy, transform: 'rotate(-1deg)' }}
        >
          {/* Decorative circles */}
          <div 
            className="absolute top-3 right-3 w-8 h-8 rounded-full border-2 flex items-center justify-center"
            style={{ backgroundColor: colors.coral, borderColor: colors.navy }}
          >
            <Circle className="w-3 h-3" style={{ color: colors.navy }} fill="currentColor" />
          </div>
          <div 
            className="absolute bottom-3 left-3 w-6 h-6 rounded-lg border-2"
            style={{ backgroundColor: colors.tealPop, borderColor: colors.navy, transform: 'rotate(15deg)' }}
          />
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-14 h-14 rounded-2xl border-2 flex items-center justify-center"
                style={{ backgroundColor: 'white', borderColor: colors.navy }}
              >
                <Sparkles className="w-7 h-7" style={{ color: colors.orange }} />
              </div>
              <div>
                <h2 className="text-2xl font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>
                  WORK JOURNAL
                </h2>
                <p className="text-sm font-bold" style={{ color: colors.teal }}>Drop your weekly wins</p>
              </div>
            </div>
            <button 
              className="w-full rounded-2xl py-4 font-black text-lg border-2 hover:scale-[1.02] transition-transform"
              style={{ 
                backgroundColor: colors.navy, 
                borderColor: colors.navy,
                color: 'white',
                fontFamily: "'Fredoka', sans-serif"
              }}
            >
              DROP A WIN! +50 PTS
            </button>
          </div>
        </div>

        {/* This Week Section */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div 
              className="w-8 h-8 rounded-lg border-2 flex items-center justify-center"
              style={{ backgroundColor: colors.tealPop, borderColor: colors.navy, transform: 'rotate(-5deg)' }}
            >
              <Calendar className="w-4 h-4" style={{ color: colors.navy }} />
            </div>
            <h2 className="text-lg font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>
              THIS WEEK
            </h2>
          </div>

          <div className="space-y-3">
            {/* Assessment Card */}
            <div 
              className="rounded-2xl p-4 border-2 cursor-pointer shadow-lg hover:scale-[1.01] transition-transform"
              style={{ backgroundColor: colors.teal, borderColor: colors.navy, transform: 'rotate(0.5deg)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-11 h-11 rounded-xl border-2 flex items-center justify-center"
                    style={{ backgroundColor: 'white', borderColor: colors.navy }}
                  >
                    <Brain className="w-5 h-5" style={{ color: colors.teal }} />
                  </div>
                  <div className="text-white">
                    <h3 className="font-black" style={{ fontFamily: "'Fredoka', sans-serif" }}>SKILLS CHECK</h3>
                    <p className="text-white/80 text-sm font-bold">Level up • 50 pts</p>
                  </div>
                </div>
                <span 
                  className="text-xs font-black px-3 py-1.5 rounded-lg border-2"
                  style={{ backgroundColor: colors.coral, borderColor: colors.navy, color: 'white' }}
                >
                  NEW!
                </span>
              </div>
            </div>

            {/* Check-in Card */}
            <div 
              className="bg-white rounded-2xl p-4 border-2 cursor-pointer shadow-md hover:shadow-lg transition-all"
              style={{ borderColor: colors.navy, transform: 'rotate(-0.5deg)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl border-2 flex items-center justify-center"
                    style={{ backgroundColor: colors.tealPop, borderColor: colors.navy }}
                  >
                    <CheckCircle className="w-5 h-5" style={{ color: colors.navy }} />
                  </div>
                  <div>
                    <h3 className="font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>CHECK-IN</h3>
                    <p className="text-sm font-bold" style={{ color: colors.teal }}>+20 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5" style={{ color: colors.navy }} />
              </div>
            </div>

            {/* Trivia Card */}
            <div 
              className="bg-white rounded-2xl p-4 border-2 cursor-pointer shadow-md hover:shadow-lg transition-all"
              style={{ borderColor: colors.navy, transform: 'rotate(0.3deg)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl border-2 flex items-center justify-center"
                    style={{ backgroundColor: colors.lavender, borderColor: colors.navy }}
                  >
                    <Zap className="w-5 h-5" style={{ color: colors.navy }} />
                  </div>
                  <div>
                    <h3 className="font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>TRIVIA</h3>
                    <p className="text-sm font-bold" style={{ color: colors.teal }}>+25 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5" style={{ color: colors.navy }} />
              </div>
            </div>
          </div>
        </div>

        {/* Team Card */}
        <div 
          className="bg-white rounded-3xl p-5 border-2 relative overflow-hidden shadow-lg"
          style={{ borderColor: colors.navy, transform: 'rotate(-0.5deg)' }}
        >
          {/* Corner sticker */}
          <div 
            className="absolute -top-1 -right-1 w-14 h-14 flex items-center justify-center"
            style={{ 
              backgroundColor: colors.orange,
              clipPath: 'polygon(100% 0, 0 0, 100% 100%)'
            }}
          >
            <Users className="w-4 h-4 text-white absolute top-2 right-2" />
          </div>
          
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 rounded-2xl border-2 flex items-center justify-center"
                  style={{ backgroundColor: colors.navy, borderColor: colors.navy }}
                >
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>TEAM ALPHA</h3>
                  <p className="text-sm font-bold" style={{ color: colors.teal }}>8 members</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-black" style={{ color: colors.orange, fontFamily: "'Fredoka', sans-serif" }}>4,500</p>
                <p className="text-xs font-bold" style={{ color: colors.teal }}>TEAM PTS</p>
              </div>
            </div>
            <button 
              className="w-full rounded-2xl py-3 font-black border-2 hover:scale-[1.02] transition-transform"
              style={{ 
                backgroundColor: colors.tealPop,
                borderColor: colors.navy, 
                color: colors.navy,
                fontFamily: "'Fredoka', sans-serif"
              }}
            >
              SEE YOUR SQUAD <ChevronRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation - Playful */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-white border-t-2 px-4 py-2"
        style={{ borderColor: colors.navy }}
      >
        <div className="max-w-md mx-auto flex justify-around items-center">
          {[
            { icon: Target, label: 'Home', active: true, color: colors.tealPop },
            { icon: Target, label: 'Goals', active: false, color: colors.orange },
            { icon: Trophy, label: 'Stats', active: false, color: colors.yellow },
            { icon: Users, label: 'Profile', active: false, color: colors.lavender },
          ].map((item) => (
            <button 
              key={item.label} 
              className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl border-2 transition-all"
              style={{ 
                backgroundColor: item.active ? item.color : 'transparent',
                borderColor: item.active ? colors.navy : 'transparent',
                color: item.active ? colors.navy : colors.teal
              }}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs font-bold">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default YASDesignV4;
